import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Badge,
  Button,
  Fade,
  Zoom,
  Divider
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import {
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import complianceApiService from '../../services/compliance/ComplianceApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Compliance = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchingAgents, setFetchingAgents] = useState(true);
  const [selectedOverviewCard, setSelectedOverviewCard] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchComplianceData();
    }
  }, [selectedAgent]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const agentsData = await dashboardApiService.getAllAgents();
      setAgents(agentsData);
      
      // Set default agent selection to first agent
      if (agentsData && agentsData.length > 0 && !selectedAgent) {
        setSelectedAgent(agentsData[0].id);
      }
    } catch (err) {
      setError('Failed to fetch agents');
      console.error('Error fetching agents:', err);
    } finally {
      setLoading(false);
      setFetchingAgents(false);
    }
  };

  const fetchComplianceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await complianceApiService.getComplianceAlerts(selectedAgent);
      setComplianceData(response);
      
      // Process the data for charts and statistics
      const processed = complianceApiService.processComplianceData(response.alerts || []);
      setProcessedData(processed);
    } catch (err) {
      setError('Failed to fetch compliance data');
      console.error('Compliance fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgent]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchComplianceData();
    setRefreshing(false);
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      case 'medium': return <InfoIcon />;
      case 'low': return <CheckCircleIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStandardsChartData = () => {
    if (!processedData || !processedData.complianceStandards) return [];
    
    return Object.entries(processedData.complianceStandards).map(([key, standard]) => ({
      name: standard.name,
      value: standard.count,
      count: standard.count,
      percentage: processedData.totalAlerts > 0 ? 
        ((standard.count / processedData.totalAlerts) * 100).toFixed(1) : 0,
      fill: complianceApiService.getStandardColor(standard.name)
    }));
  };

  const getSeverityChartData = () => {
    if (!processedData || !processedData.severityDistribution) return [];
    
    return Object.entries(processedData.severityDistribution).map(([severity, count]) => ({
      name: severity,
      value: count,
      count: count,
      percentage: processedData.totalAlerts > 0 ? 
        ((count / processedData.totalAlerts) * 100).toFixed(1) : 0,
      fill: complianceApiService.getSeverityColor(severity)
    }));
  };

  const getEnhancedAlerts = () => {
    if (!complianceData?.alerts) return [];
    return complianceApiService.getEnhancedRecentAlerts(complianceData.alerts);
  };

  const handleStandardClick = (data) => {
    setSelectedStandard(selectedStandard === data.name ? null : data.name);
  };

  const handleSeverityClick = (data) => {
    setSelectedSeverity(selectedSeverity === data.name ? null : data.name);
  };

  const handleOverviewCardClick = (cardType) => {
    if (selectedOverviewCard === cardType) {
      setSelectedOverviewCard(null);
      setShowDetailedView(false);
    } else {
      setSelectedOverviewCard(cardType);
      setShowDetailedView(true);
    }
  };

  const getDetailedViewData = () => {
    if (!processedData || !selectedOverviewCard) return null;

    switch (selectedOverviewCard) {
      case 'totalAlerts':
        return {
          title: 'Total Alerts Details',
          data: getEnhancedAlerts().slice(0, 10),
          type: 'alerts'
        };
      case 'standardsAffected':
        return {
          title: 'Standards Affected Details',
          data: Object.entries(processedData.complianceStandards).map(([key, standard]) => ({
            name: standard.name,
            count: standard.count,
            violations: standard.violations
          })),
          type: 'standards'
        };
      case 'criticalHigh':
        return {
          title: 'Critical & High Severity Alerts',
          data: getEnhancedAlerts().filter(alert => 
            alert.severity.toLowerCase() === 'critical' || alert.severity.toLowerCase() === 'high'
          ).slice(0, 10),
          type: 'alerts'
        };
      case 'mediumSeverity':
        return {
          title: 'Medium Severity Alerts',
          data: getEnhancedAlerts().filter(alert => 
            alert.severity.toLowerCase() === 'medium'
          ).slice(0, 10),
          type: 'alerts'
        };
      default:
        return null;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: 2,
            padding: 2,
            boxShadow: 3
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {data.name}
          </Typography>
          <Typography variant="body2">
            Count: {data.count}
          </Typography>
          <Typography variant="body2">
            Percentage: {data.percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Compliance Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Monitor compliance violations and standards across your infrastructure
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {/* Agent Selection */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Agent Selection
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={9}>
              <FormControl fullWidth>
                <InputLabel>Select Agent</InputLabel>
                <Select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  label="Select Agent"
                  disabled={fetchingAgents}
                  sx={{
                    backgroundColor: 'white',
                    height: '56px',
                    fontSize: '1rem',
                    '& .MuiSelect-select': {
                      padding: '16px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '56px',
                      boxSizing: 'border-box'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '2px',
                      borderColor: '#d1d5db'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    }
                  }}
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Chip
                          label={agent.id}
                          size="small"
                          color="primary"
                          sx={{ mr: 2, minWidth: '60px' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'medium', mr: 2 }}>
                          {agent.name}
                        </Typography>
                        <Chip
                          label={agent.ip}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={fetchComplianceData}
                disabled={!selectedAgent || loading || fetchingAgents}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
                sx={{
                  height: '56px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4f46e5, #7c3aed)'
                  }
                }}
              >
                {loading ? 'Loading...' : 'ANALYZE COMPLIANCE'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {processedData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                cursor: 'pointer',
                transform: selectedOverviewCard === 'totalAlerts' ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                border: selectedOverviewCard === 'totalAlerts' ? '3px solid #fff' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
              onClick={() => handleOverviewCardClick('totalAlerts')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {processedData.totalAlerts}
                    </Typography>
                    <Typography variant="body2">
                      Total Alerts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                color: 'white',
                cursor: 'pointer',
                transform: selectedOverviewCard === 'standardsAffected' ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                border: selectedOverviewCard === 'standardsAffected' ? '3px solid #fff' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
              onClick={() => handleOverviewCardClick('standardsAffected')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {Object.keys(processedData.complianceStandards).length}
                    </Typography>
                    <Typography variant="body2">
                      Standards Affected
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                color: 'white',
                cursor: 'pointer',
                transform: selectedOverviewCard === 'criticalHigh' ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                border: selectedOverviewCard === 'criticalHigh' ? '3px solid #fff' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
              onClick={() => handleOverviewCardClick('criticalHigh')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {(processedData.severityDistribution.Critical || 0) + (processedData.severityDistribution.High || 0)}
                    </Typography>
                    <Typography variant="body2">
                      Critical & High
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
                color: 'white',
                cursor: 'pointer',
                transform: selectedOverviewCard === 'mediumSeverity' ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                border: selectedOverviewCard === 'mediumSeverity' ? '3px solid #fff' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
              onClick={() => handleOverviewCardClick('mediumSeverity')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WarningIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {processedData.severityDistribution.Medium || 0}
                    </Typography>
                    <Typography variant="body2">
                      Medium Severity
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Detailed View for Selected Overview Card */}
      {showDetailedView && getDetailedViewData() && (
        <Fade in={showDetailedView}>
          <Card sx={{ mb: 4, border: '2px solid #6366f1' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  {getDetailedViewData().title}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedOverviewCard(null);
                    setShowDetailedView(false);
                  }}
                >
                  Close
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              {getDetailedViewData().type === 'alerts' ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Agent</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Standards</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDetailedViewData().data.map((alert, index) => (
                        <TableRow key={alert.id || index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTimestamp(alert.timestamp)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {alert.agent?.name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {alert.agent?.ip || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              maxWidth: 300, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis'
                            }}>
                              {alert.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={alert.severity}
                              color={getSeverityColor(alert.severity)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {alert.standards?.map((standard, idx) => (
                                <Chip
                                  key={idx}
                                  label={standard.name}
                                  size="small"
                                  variant="outlined"
                                />
                              )) || 'N/A'}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Grid container spacing={2}>
                  {getDetailedViewData().data.map((standard, index) => (
                    <Grid item xs={12} md={6} lg={4} key={index}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {standard.name}
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Chip 
                              label={`${standard.count} Alerts`} 
                              color="primary" 
                              sx={{ mr: 1 }} 
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Recent violations: {standard.violations?.slice(0, 3).join(', ') || 'None'}
                            {standard.violations?.length > 3 && '...'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Charts Section */}
      {processedData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Compliance Standards Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Compliance Standards Distribution
                </Typography>
                <Divider sx={{ mb: 3 }} />
    
                <Box sx={{ height: 400, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getStandardsChartData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                        onClick={handleStandardClick}
                        style={{ cursor: "pointer" }}
                      >
                        {getStandardsChartData().map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                            stroke={selectedStandard === entry.name ? "#333" : "none"}
                            strokeWidth={selectedStandard === entry.name ? 3 : 0}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
    
          {/* Severity Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", width: "400px" }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Severity Distribution
                </Typography>
                <Divider sx={{ mb: 3 }} />
    
                <Box sx={{ height: 400, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getSeverityChartData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                        onClick={handleSeverityClick}
                        style={{ cursor: "pointer" }}
                      >
                        {getSeverityChartData().map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                            stroke={selectedSeverity === entry.name ? "#333" : "none"}
                            strokeWidth={selectedSeverity === entry.name ? 3 : 0}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Compliance Standards Details */}
      {processedData && Object.keys(processedData.complianceStandards).length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Compliance Standards Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {Object.entries(processedData.complianceStandards).map(([key, standard]) => (
                <Grid item xs={12} md={6} lg={4} key={key}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {standard.name}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label={`${standard.count} Alerts`} 
                          color="primary" 
                          sx={{ mr: 1 }} 
                        />
                        <Chip 
                          label={`${standard.violationCount} Violations`} 
                          color="secondary" 
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Recent violations: {standard.violations.slice(0, 3).join(', ')}
                        {standard.violations.length > 3 && '...'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Recent Alerts Table */}
      {getEnhancedAlerts().length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Recent Compliance Alerts
              </Typography>
              {(selectedStandard || selectedSeverity) && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedStandard && (
                    <Chip
                      label={`Standard: ${selectedStandard}`}
                      onDelete={() => setSelectedStandard(null)}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {selectedSeverity && (
                    <Chip
                      label={`Severity: ${selectedSeverity}`}
                      onDelete={() => setSelectedSeverity(null)}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Standards</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getEnhancedAlerts()
                    .filter(alert => {
                      if (selectedStandard && !alert.standards.some(s => s.name === selectedStandard)) {
                        return false;
                      }
                      if (selectedSeverity && alert.severity !== selectedSeverity) {
                        return false;
                      }
                      return true;
                    })
                    .map((alert) => (
                    <TableRow key={alert.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          {formatTimestamp(alert.timestamp)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {alert.agent.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {alert.agent.ip}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={alert.fullLog} arrow>
                          <Typography variant="body2" sx={{ 
                            maxWidth: 300, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            cursor: 'help'
                          }}>
                            {alert.description}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={alert.severity}
                          color={getSeverityColor(alert.severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {alert.standards.map((standard, index) => (
                            <Chip
                              key={index}
                              label={standard.name}
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedStandard(standard.name)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {alert.geoLocation && (
                            <Tooltip title={`${alert.geoLocation.city_name}, ${alert.geoLocation.country_name}`}>
                              <LocationIcon fontSize="small" color="action" />
                            </Tooltip>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {alert.location}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Instructions Card */}
      {!complianceData && !loading && (
        <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SecurityIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Welcome to Compliance Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Select an agent from the dropdown above and click "Analyze Compliance" to view compliance violations and standards
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<SecurityIcon />} label="PCI DSS" variant="outlined" />
              <Chip icon={<AssessmentIcon />} label="HIPAA" variant="outlined" />
              <Chip icon={<WarningIcon />} label="GDPR" variant="outlined" />
              <Chip icon={<InfoIcon />} label="NIST 800-53" variant="outlined" />
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Compliance;
